// utils/siteTagFormats.ts
//
// Site tag artefacts and export formatting, for the two deployment modes the
// Site Tags tab offers: a website, or an app.
//
// Pure functions — no React, no MUI, no imports. Everything is derived from
// `customer_id` and `id`, both already present on SiteTagResponse, so nothing
// here needs a backend change.
//
// Two things to know before editing:
//
//  1. buildHtmlTag reproduces the backend's computed `tag` field byte for byte
//     (app/schemas/site_tag.py). If that changes server-side, change this to
//     match or the table and the clipboard will drift apart silently.
//
//  2. The cachebuster is belt and braces, not the only defence — measured
//     2026-08-10. The endpoint returns `cache-control: no-store, no-cache,
//     must-revalidate, max-age=0`, plus `pragma: no-cache` and `expires: 0`, so
//     any conforming client re-requests every time. The snippets keep their
//     cachebuster anyway: a client with its own cache layer, or an intermediary
//     that ignores the header, is not hypothetical, and a request that never
//     leaves the device cannot be recovered afterwards. Extra fires, by
//     contrast, are free — the correlation deduplicates by IP within the
//     attribution window. Bias towards firing.

// ── Types ─────────────────────────────────────────────────────────────────

/** What the Website | App toggle selects. Governs the notes and the clipboard. */
export type TagMode = 'website' | 'app';

/** The three app flavours, resolved by the developer, not by us. */
export type AppPlatform = 'webview' | 'reactNative' | 'native';

/** Minimal shape a tag row must satisfy. `SiteTag` conforms structurally. */
export interface SiteTagLike {
    id: number;
    customer_id: number;
    tag_description: string;
}

export interface TagExportContext {
    customerName?: string | null;
    hasCsp?: boolean | null;
    cspWhitelisted?: boolean | null;
    consentGated?: boolean | null;
    /** The detected CMP, when there is one. Names the gate rather than alluding to it. */
    cmpVendor?: string | null;
    /** Injectable for tests; defaults to now. */
    generatedAt?: Date;
}

// ── Site detection verdicts ───────────────────────────────────────────────
//
// The backend's detection cron writes into `tag_manager`, `has_csp`,
// `csp_whitelisted` and `cmp_vendor`. Two of those fields carry meanings that are
// not obvious from their names, and getting them wrong puts a falsehood on screen,
// so the interpretation lives here once rather than in each component that renders it.

/**
 * Values the detector writes into `tag_manager` that are NOT tag managers. They
 * describe *our* fetch of the customer's site — DNS gone, WAF 403, 429, timeout —
 * and there is nothing the viewer can do about any of them. Rendered raw they read
 * as product names: 24 of 162 customers on alpha currently sit in this state, so
 * the Site Detection panel confidently reports a tag manager called "unreachable".
 */
const TAG_MANAGER_FETCH_FAILURES = new Set(['dead', 'blocked', 'rate_limited', 'unreachable']);

export type TagManagerState = 'detected' | 'absent' | 'undetermined' | 'unscanned';

export interface TagManagerVerdict {
    state: TagManagerState;
    /** Safe to render. Never a sentinel, never a raw null. */
    label: string;
    /** The real product name when there is one — for accenting deployment notes. */
    name: string | null;
}

/**
 * Three distinct kinds of "nothing to show", which the UI previously flattened into
 * one "Not detected":
 *
 *   unscanned    — NULL; we have not looked yet
 *   absent       — we looked and the site runs no tag manager
 *   undetermined — we looked and could not read the site at all
 *
 * A client asking "why does it say nothing?" deserves the different answer, and only
 * the middle one is a fact about *them*.
 */
export const resolveTagManager = (raw: string | null | undefined): TagManagerVerdict => {
    const value = (raw ?? '').trim();
    if (!value) return { state: 'unscanned', label: 'Not scanned', name: null };

    const lower = value.toLowerCase();
    if (lower === 'none') return { state: 'absent', label: 'None detected', name: null };
    if (TAG_MANAGER_FETCH_FAILURES.has(lower)) return { state: 'undetermined', label: 'Not determined', name: null };

    return { state: 'detected', label: value, name: value };
};

export type CspState = 'unchecked' | 'unrestricted' | 'whitelisted' | 'blocking' | 'unverified';

export interface CspVerdict {
    state: CspState;
    label: string;
}

/**
 * `has_csp` does NOT mean "a CSP header exists". The backend records True only for a
 * *restrictive* policy — one that actually restricts something we care about — so
 * False means "nothing in our way", covering both a permissive CSP and no CSP at all.
 * 123 of 162 customers sit in that bucket, so the old "Not detected" was the most-shown
 * label on the panel and it asserted something we no longer know.
 *
 * `csp_whitelisted` is only meaningful when `has_csp` is true, which is why it is read
 * solely inside that branch — 5 customers currently carry a stale `false` alongside
 * `has_csp: false`, and it must not be allowed to colour the verdict.
 */
export const resolveCsp = (hasCsp: boolean | null | undefined, whitelisted: boolean | null | undefined): CspVerdict => {
    if (hasCsp == null) return { state: 'unchecked', label: 'Unknown' };
    if (hasCsp === false) return { state: 'unrestricted', label: 'No restriction' };
    if (whitelisted === true) return { state: 'whitelisted', label: 'Whitelisted' };
    if (whitelisted === false) return { state: 'blocking', label: 'Blocking tag' };
    return { state: 'unverified', label: 'Detected — check whitelist' };
};

export interface ConsentVerdict {
    gated: boolean | null;
    label: string;
}

/**
 * `cmp_vendor` is non-null exactly when `consent_gated` is true (verified across all
 * 162 customers on alpha), but the fallback is kept: an unnamed gate is still a gate,
 * and the caveat matters more than the vendor's name.
 */
export const resolveConsent = (
    consentGated: boolean | null | undefined,
    cmpVendor: string | null | undefined
): ConsentVerdict => {
    if (consentGated === true) {
        const vendor = (cmpVendor ?? '').trim();
        return { gated: true, label: vendor ? `Gated — ${vendor}` : 'Gated — limited' };
    }
    if (consentGated === false) return { gated: false, label: 'Not gated' };
    return { gated: null, label: 'Unknown' };
};

// ── Constants ─────────────────────────────────────────────────────────────

const ENDPOINT = 'https://sites.mimeanalytics.com/response';
const ORIGIN = 'https://sites.mimeanalytics.com';
const RULE = '----------------------------------------';

/** An <img> is governed by img-src. A fetch() is governed by connect-src. */
export const CSP_IMG_SRC = `img-src 'self' ${ORIGIN};`;
export const CSP_CONNECT_SRC = `connect-src 'self' ${ORIGIN};`;

// ── Per-artefact builders ─────────────────────────────────────────────────

/**
 * The browser pixel. Byte-identical to SiteTagResponse.tag, including the empty
 * trailing cachebuster, which is safe because the endpoint sends `no-store` —
 * measured 2026-08-10, see note 2 above.
 *
 * Do not "fix" the empty value with GTM's `{{Random Number}}`. This one artefact
 * is also pasted into raw HTML and into Adobe Launch, and neither expands GTM
 * macro syntax — both would ship the literal braces as a fixed cachebuster,
 * which is worse than empty because it looks deliberate.
 */
export function buildHtmlTag(tag: SiteTagLike): string {
    return (
        `<img alt='' src='${ENDPOINT}?client=${tag.customer_id}&site=${tag.id}&cachebuster=' ` +
        `style='width:1px; height:1px; border:none; position:absolute; top:-9999px; left:-9999px;'>`
    );
}

/**
 * The request without a cachebuster — the tag's identity, and what the Tag
 * column shows. Deployment artefacts are copied from the notes or the export,
 * so the column is free to display the part that actually distinguishes one
 * row from another rather than a truncated <img> wrapper.
 */
export function buildTagUrl(tag: SiteTagLike): string {
    return `${ENDPOINT}?client=${tag.customer_id}&site=${tag.id}`;
}

/**
 * The bare request a native client makes. `<unique>` is a placeholder on
 * purpose: a literal value here would be pasted as a constant, and every
 * request after the first would be served from the platform HTTP cache.
 */
export function buildResponseUrl(tag: SiteTagLike): string {
    return `${buildTagUrl(tag)}&cachebuster=<unique>`;
}

/**
 * The JavaScript helper, in one of two scopings.
 *
 * 'webview' assigns to `window` explicitly. A bare `function` declaration
 * inside a bundled module is module-scoped, so a host app calling
 * evaluateJavascript("infinitumResponse(…)") would find nothing there.
 *
 * 'reactNative' exports a module function. RN's fetch is whatwg-fetch over the
 * native networking stack: `mode`, `cache` and `keepalive` are accepted and
 * discarded, so they are omitted rather than left in to mislead.
 */
export function buildJsHelper(platform: 'webview' | 'reactNative'): string {
    if (platform === 'reactNative') {
        return [
            'export function infinitumResponse(client, site) {',
            "    const url = '" + ENDPOINT + "'",
            "        + '?client=' + client + '&site=' + site",
            "        + '&cachebuster=' + Date.now() + '_'",
            '        + Math.random().toString(36).slice(2, 10);',
            '',
            "    fetch(url, { method: 'GET' }).catch(function () {});",
            '}',
        ].join('\n');
    }

    return [
        'window.infinitumResponse = function (client, site) {',
        "    var url = '" + ENDPOINT + "'",
        "        + '?client=' + client + '&site=' + site",
        "        + '&cachebuster=' + Date.now() + '_'",
        '        + Math.random().toString(36).slice(2, 10);',
        '',
        '    try {',
        "        fetch(url, { method: 'GET', mode: 'no-cors', cache: 'no-store', keepalive: true })",
        '            .catch(function () {});',
        '    } catch (e) {}',
        '};',
    ].join('\n');
}

/** The call site the developer pastes at the firing point. */
export function buildJsCall(tag: SiteTagLike): string {
    return 'infinitumResponse(' + tag.customer_id + ', ' + tag.id + ');';
}

/**
 * Every tag as a finished call, each under its own name — the same block shape
 * the website export uses per tag.
 *
 * The app sections used to show one call and label it "e.g.". The website mode
 * asserts three finished <img> tags; app mode asserted a helper and left the
 * reader to infer how many calls there were and to go back up the document for
 * the numbers. A worked example is what you write when you cannot know the
 * values. We know the values.
 */
export function buildCallList(
    tags: SiteTagLike[],
    render: (tag: SiteTagLike) => string,
    empty = '(no site tags defined yet)'
): string {
    if (!tags.length) return empty;
    return tags.map((tag) => `${tag.tag_description}\n${render(tag)}`).join('\n\n');
}

/**
 * The iOS reference implementation, and separately the call. One `Infinitum`
 * enum serves every tag, so binding the implementation to a single tag — as
 * this did, via a trailing `// Fire it` line — misrepresented the structure.
 */
export function buildSwiftSnippet(): string {
    return [
        'import Foundation',
        '',
        'enum Infinitum {',
        '    private static let session = URLSession(configuration: .ephemeral)',
        '',
        '    static func response(client: Int, site: Int) {',
        '        let cachebuster = "\\(Int(Date().timeIntervalSince1970 * 1000))_\\(UUID().uuidString.prefix(8))"',
        '        guard let url = URL(string:',
        '            "' + ENDPOINT + '?client=\\(client)&site=\\(site)&cachebuster=\\(cachebuster)"',
        '        ) else { return }',
        '',
        '        var request = URLRequest(url: url)',
        '        request.cachePolicy = .reloadIgnoringLocalCacheData',
        '        session.dataTask(with: request).resume()',
        '    }',
        '}',
    ].join('\n');
}

export function buildSwiftCall(tag: SiteTagLike): string {
    return `Infinitum.response(client: ${tag.customer_id}, site: ${tag.id})`;
}

export function buildKotlinSnippet(): string {
    return [
        'import okhttp3.*',
        'import java.io.IOException',
        'import java.util.UUID',
        '',
        'object Infinitum {',
        '    // One shared client. Do not construct an OkHttpClient per call.',
        '    private val http = OkHttpClient()',
        '',
        '    fun response(client: Int, site: Int) {',
        '        val cachebuster = "${System.currentTimeMillis()}_${UUID.randomUUID().toString().take(8)}"',
        '        val url = "' + ENDPOINT + '?client=$client&site=$site&cachebuster=$cachebuster"',
        '',
        '        val request = Request.Builder()',
        '            .url(url)',
        '            .cacheControl(CacheControl.FORCE_NETWORK)',
        '            .build()',
        '',
        '        http.newCall(request).enqueue(object : Callback {',
        '            override fun onFailure(call: Call, e: IOException) {}',
        '            override fun onResponse(call: Call, response: Response) = response.close()',
        '        })',
        '    }',
        '}',
        '',
        '// AndroidManifest.xml requires:',
        '// <uses-permission android:name="android.permission.INTERNET" />',
    ].join('\n');
}

export function buildKotlinCall(tag: SiteTagLike): string {
    return `Infinitum.response(client = ${tag.customer_id}, site = ${tag.id})`;
}

// ── Shared copy — rendered by the notes panels and the clipboard alike ─────

/**
 * The classification question. It exists so that someone in Sales or AdOps can
 * hand this to a developer without knowing what Capacitor is — they answer
 * "it's an app", the developer answers which kind.
 */
export const APP_DIAGNOSTIC = {
    question: 'Which one is this app? Ask the developer: does the app source contain an index.html?',
    answers: [
        { answer: 'Yes', platform: 'App with a WebView' },
        { answer: 'No, but the app is written in JavaScript', platform: 'React Native / Expo' },
        { answer: 'No JavaScript at all', platform: 'Native app' },
    ],
} as const;

export interface AppPlatformMeta {
    id: AppPlatform;
    label: string;
    examples: string;
}

export const APP_PLATFORMS: readonly AppPlatformMeta[] = [
    {
        id: 'webview',
        label: 'App with a WebView',
        examples: 'Capacitor, Cordova, Ionic, or a native shell wrapped around a web app.',
    },
    {
        id: 'reactNative',
        label: 'React Native / Expo',
        examples: 'React Native, Expo.',
    },
    {
        id: 'native',
        label: 'Native app',
        examples: 'Swift or SwiftUI on iOS, Kotlin or Java on Android, Flutter — any HTTP client will do.',
    },
] as const;

/**
 * Who the parcel is for.
 *
 * The export is read by nobody who can act on it. It leaves here in the hands of
 * a broadcaster, is forwarded to an agency, forwarded again to a marketing
 * manager, and only then reaches the developer it was written for. None of those
 * first three can answer "does the app source contain an index.html" — they are a
 * relay, and the document never told them so. An addressed parcel gets forwarded;
 * an unaddressed one gets read, not understood, and put down.
 */
export const EXPORT_RECIPIENT: Record<TagMode, string> = {
    website: [
        'FOR WHOEVER MANAGES THE WEBSITE — their developer, or whoever administers the tag',
        'manager. Please forward this document to them whole; it is written for them.',
    ].join('\n'),
    app: [
        'FOR WHOEVER BUILDS THE APP. Please forward this document to them whole; it is',
        'written for them. It covers all three kinds of app and explains how to tell which',
        'one you have, so there is nothing you need to answer before sending it on.',
    ].join('\n'),
};

/**
 * `<unique>` appears in the very first thing a developer reads — the tag list at the
 * head of the export — and was not explained until the closing WHEN TO FIRE section,
 * some hundred lines later. Two failure modes in that gap: paste it literally, or
 * substitute one fixed value and think the job done. Both look like a working
 * integration and record nothing.
 */
export const CACHEBUSTER_NOTE = [
    '<unique> above is a placeholder, not a value. Replace it with something different on',
    'every single request — a timestamp and a few random characters is ample. It is the only',
    'thing preventing the HTTP client from serving its own cached copy, in which case the',
    // Position-neutral: this sits above the snippets in the export and below them
    // on screen, so it cannot say "below".
    'request never leaves the device and the visit is never recorded. The ready-made snippets',
    'do this for you.',
].join('\n');

/**
 * Deliberately permissive. Over-firing is deduplicated server-side and costs
 * nothing; under-firing loses the match outright. A developer who half
 * implements a session guard is worse off than one with no guard to implement.
 *
 * "Call it" had one antecedent while the sections showed a single example call.
 * Now that every tag is listed, the site-wide tag and the event tags fire at
 * different moments and the rule has to say which is which.
 */
export const FIRING_RULE = [
    'The site-wide tag — the one covering the whole app — fires when the app comes to the',
    'foreground. Every other tag is named for the moment it marks, and fires at that moment:',
    'a tag called "Start of Ticket Sale" fires when a ticket sale starts.',
    '',
    'Calling any of them more often than that is harmless — repeat visits from the same IP',
    'inside the attribution window are deduplicated. Calling them too rarely is not harmless.',
    '',
    'Do not rely on the app being cold-started. A backgrounded app is usually resumed rather',
    'than relaunched, so "once per launch" can mean once a week.',
    '',
    'Every request must carry a fresh cachebuster value. Without one, the HTTP client will',
    'serve the response from its own cache and the visit is never recorded.',
].join('\n');

export const STORE_REQUIREMENTS = [
    'Apple — declare the network call under App Privacy. The tag collects the IP address the',
    'request arrives from, and nothing else: no advertising identifier, no device fingerprint.',
    'Google Play — the same disclosure under Data safety, plus the INTERNET permission in',
    'AndroidManifest.xml.',
].join('\n');

const GTM_INSTRUCTIONS = [
    'Instructions for Google Tag Manager (GTM):',
    '1. Create a new Tag in GTM.',
    '2. Select "Custom HTML" as the tag type.',
    '3. Paste the Tag Code from above.',
    '4. Set the Trigger to "All Pages" (or specific pages as required).',
    '5. Save and Publish.',
].join('\n');

const ADOBE_INSTRUCTIONS = [
    'Instructions for Adobe Experience Platform Tags (Adobe Launch):',
    '1. In Adobe Experience Platform Data Collection, open your property.',
    '2. Create a new Rule (e.g. "All Pages — Infinitum tag").',
    '3. Under Actions, add: Core → Custom Code, type HTML.',
    '4. Paste the Tag Code from above into the code editor.',
    '5. Save the rule, add it to your working Library, then build and publish.',
].join('\n');

// ── Conditional sections ──────────────────────────────────────────────────

function buildWebsiteCspSection(ctx: TagExportContext): string {
    const directive = `  ${CSP_IMG_SRC}`;

    if (ctx.hasCsp === true && ctx.cspWhitelisted === true) {
        return (
            'Content Security Policy (CSP): detected and Infinitum is already whitelisted.\n' +
            'No CSP action required.'
        );
    }
    if (ctx.hasCsp === true && ctx.cspWhitelisted === false) {
        return (
            '⚠ URGENT — Content Security Policy (CSP) is BLOCKING the tag:\n' +
            'This site enforces a CSP header and the Infinitum domain is NOT whitelisted.\n' +
            'The tag is currently being silently blocked.\n' +
            "Add the following to the site's Content-Security-Policy header:\n" +
            directive
        );
    }
    if (ctx.hasCsp === true) {
        return (
            '⚠ IMPORTANT — Content Security Policy (CSP) detected:\n' +
            'This site enforces a CSP header. The tag WILL be silently blocked\n' +
            'unless the Infinitum domain is whitelisted.\n' +
            "Add the following to the site's Content-Security-Policy header:\n" +
            directive
        );
    }
    return (
        'Content Security Policy (CSP) — if required:\n' +
        "Add the following to the site's Content-Security-Policy header:\n" +
        directive
    );
}

function buildConsentSection(ctx: TagExportContext): string | null {
    if (ctx.consentGated !== true) return null;
    const vendor = (ctx.cmpVendor ?? '').trim();
    const platform = vendor ? `${vendor}, a consent management platform` : 'a consent management platform';
    return [
        'Consent-gated measurement:',
        `This client deploys the Infinitum pixel through ${platform}, so it`,
        'fires only for visitors who accept tracking. Recorded visits are therefore a fraction',
        'of true traffic and response rates are measurement-limited — a suppressed figure',
        'reflects the consent gate, not campaign underperformance.',
    ].join('\n');
}

function buildDiagnosticSection(): string {
    const width = Math.max(...APP_DIAGNOSTIC.answers.map((a) => a.answer.length));
    const rows = APP_DIAGNOSTIC.answers.map((a) => `  ${a.answer.padEnd(width)}  ->  ${a.platform}`).join('\n');

    return (
        'WHICH KIND OF APP IS THIS?\n' +
        'Ask the developer: does the app source contain an index.html?\n\n' +
        rows +
        '\n\nUse the section below that matches. Ignore the other two.'
    );
}

/** Heads every call list. Says there are several and that they fire separately. */
export const CALL_LIST_LEAD = 'Then fire each tag at its moment:';

function buildAppSections(tags: SiteTagLike[]): string {
    const jsCalls = buildCallList(tags, buildJsCall, 'infinitumResponse(<client>, <site>);');

    const webview = [
        'APP WITH A WEBVIEW',
        APP_PLATFORMS[0].examples,
        '',
        'Include this once:',
        buildJsHelper('webview'),
        '',
        CALL_LIST_LEAD,
        '',
        jsCalls,
        '',
        'Content Security Policy — only if the WebView page enforces one. Both directives are',
        'needed: the pixel is an image, the helper is a fetch.',
        `  ${CSP_IMG_SRC}`,
        `  ${CSP_CONNECT_SRC}`,
    ].join('\n');

    const reactNative = [
        'REACT NATIVE / EXPO',
        APP_PLATFORMS[1].examples,
        '',
        'Include this once:',
        buildJsHelper('reactNative'),
        '',
        CALL_LIST_LEAD,
        '',
        jsCalls,
        '',
        'Do not fire the tag with an <Image> component — React Native caches images',
        'aggressively and the request will not repeat.',
        '',
        'No Content Security Policy applies to a React Native fetch — there is no browser',
        'context to enforce one. If the app also renders a WebView, the page inside that',
        'WebView is subject to a CSP; see the WebView section above.',
    ].join('\n');

    const native = [
        'NATIVE APP',
        APP_PLATFORMS[2].examples,
        '',
        'Reference implementation — iOS:',
        buildSwiftSnippet(),
        '',
        CALL_LIST_LEAD,
        '',
        buildCallList(tags, buildSwiftCall),
        '',
        'Reference implementation — Android:',
        buildKotlinSnippet(),
        '',
        CALL_LIST_LEAD,
        '',
        buildCallList(tags, buildKotlinCall),
        '',
        'No Content Security Policy applies to a native client.',
    ].join('\n');

    return [webview, reactNative, native].join(`\n\n${RULE}\n\n`);
}

// ── Export builder ────────────────────────────────────────────────────────

/** The clipboard text. Mirrors whichever mode the notes panel is showing. */
export function buildTagExport(tags: SiteTagLike[], mode: TagMode, ctx: TagExportContext = {}): string {
    const generated = (ctx.generatedAt ?? new Date()).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });

    let out = 'Infinitum Site Tags Export\n';
    if (ctx.customerName) out += `Customer: ${ctx.customerName}\n`;
    out += `Platform: ${mode === 'app' ? 'App' : 'Website'}\n`;
    out += `Generated: ${generated}\n`;
    out += `\n${EXPORT_RECIPIENT[mode]}\n`;
    out += `${RULE}\n\n`;

    tags.forEach((tag) => {
        out += `${tag.tag_description}\n`;
        out += `${mode === 'app' ? buildResponseUrl(tag) : buildHtmlTag(tag)}\n\n`;
        out += `${RULE}\n\n`;
    });

    if (mode === 'website') {
        out += `${GTM_INSTRUCTIONS}\n\n`;
        out += `${ADOBE_INSTRUCTIONS}\n\n`;
        out += `${buildWebsiteCspSection(ctx)}\n`;
    } else {
        out += `${CACHEBUSTER_NOTE}\n\n`;
        out += `${RULE}\n\n`;
        out += `${buildDiagnosticSection()}\n\n`;
        out += `${RULE}\n\n`;
        out += `${buildAppSections(tags)}\n\n`;
        out += `${RULE}\n\n`;
        out += `WHEN TO FIRE\n${FIRING_RULE}\n\n`;
        out += `APP STORE DISCLOSURE\n${STORE_REQUIREMENTS}\n`;
    }

    const consent = buildConsentSection(ctx);
    if (consent) out += `\n${consent}\n`;

    return out.trim();
}
